import { useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { ApiError, CreateChronologyResponse, useCreateChronology } from '@/api/mutations/useCreateChronology';
import { Icon } from '@/new/components/icon';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface ActionButtonProps {
  title: string;
  description: string | React.ReactNode;
  icon: React.ReactNode;
  onClick: () => void;
}

const ActionButton = ({ title, description, icon, onClick }: ActionButtonProps) => {
  return (
    <button
      className={`flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-all duration-200 hover:cursor-pointer hover:border-blue-200 hover:bg-blue-50/30 hover:shadow`}
      onClick={onClick}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-50">
        {icon}
      </div>
      <div className="text-left">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-600">{description}</p>
      </div>
    </button>
  );
};

interface OverviewCreateChronologyModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string | null;
  totalKeyFacts: number;
  totalRelevantFacts: number;
}

export function OverviewCreateChronologyModal({
  isOpen,
  onOpenChange,
  caseId,
  totalKeyFacts,
  totalRelevantFacts,
}: OverviewCreateChronologyModalProps) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const onSuccess = (data: CreateChronologyResponse) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('chronologyId', data.chronologyId);
    searchParams.set('page', '1');
    navigate(`/matters/${matterId}/subsets/${subsetId}/facts?${searchParams.toString()}`);
  };
  const onError = (error: ApiError) => {
    console.error('Mutation failed:', error);
    setIsLoading(false);
  };
  const { createChronology } = useCreateChronology({ onSuccess, onError });

  const handleChronologyCreation = async (type: string) => {
    setIsLoading(true);
    createChronology.mutate({
      caseId,
      chronologyId: null,
      type: type,
    });
  };

  const handleCustomChronology = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('chronologyTour', 'true');
    navigate(`/matters/${matterId}/subsets/${subsetId}/facts?${searchParams.toString()}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--git-fork" />
            Create Chronology
          </DialogTitle>
        </DialogHeader>
        <>
          {isLoading ? (
            <div className="flex h-80 flex-col items-center justify-center gap-6 p-6">
              <p className="text-sm text-gray-600">Please wait while we construct your chronology...</p>
              <Icon className="iconify lucide--loader-circle animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-6 text-sm leading-relaxed text-gray-600">
                Create a chronology from our preset options, or discover how to create your own from the facts table.
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <ActionButton
                    title="Key Facts"
                    description={
                      <span>
                        Create chronology from <b className="text-blue-500">{totalKeyFacts}</b> key facts.
                      </span>
                    }
                    icon={<Icon className="iconify lucide--key text-yellow-500" />}
                    onClick={() => {
                      handleChronologyCreation('keyFacts');
                      trackCustomEvent(TrackingEvents.CreateKeyFactsChronology, { caseId });
                    }}
                  />
                  <ActionButton
                    title="Relevant Facts"
                    description={
                      <span>
                        Create chronology from <b className="text-blue-500">{totalRelevantFacts}</b> relevant facts.
                      </span>
                    }
                    icon={<Icon className="iconify lucide--list text-blue-500" />}
                    onClick={() => {
                      handleChronologyCreation('relevantFacts');
                      trackCustomEvent(TrackingEvents.CreateRelevantFactsChronology, { caseId });
                    }}
                  />
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <ActionButton
                    title="Custom"
                    description="See how you can build your own chronology."
                    icon={<Icon className="iconify lucide--cog" />}
                    onClick={() => {
                      handleCustomChronology();
                      trackCustomEvent(TrackingEvents.ViewCustomChronologyFromSummary, { caseId });
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
}
